import React, { useRef } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Center,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { FaImage } from "react-icons/fa";
import { formatEmotionalState } from "../../utils/reviewLabels";
import MiniTradeTable from "./MiniTradeTable";

const emotionColor = (state) => {
  switch (state) {
    case "calm": return "green";
    case "excited": return "yellow";
    case "anxious": return "orange";
    case "fearful": return "purple";
    default: return "red";
  }
};

const ReviewCard = ({ review, onScreenshotClick, reviewCardBg, reviewNoteColor }) => (
  <Box
    mb={2}
    p={2}
    borderWidth="1px"
    borderRadius="sm"
    bg={reviewCardBg}
    position="relative"
  >
    {review.screenshot_url && (
      <Tooltip label="View screenshot" fontSize="xs" hasArrow>
        <IconButton
          icon={<FaImage />}
          size="xs"
          variant="ghost"
          aria-label="View screenshot"
          position="absolute"
          top={1}
          right={1}
          onClick={() => onScreenshotClick(review.screenshot_url)}
        />
      </Tooltip>
    )}
    <HStack spacing={3} flexWrap="wrap" pr={review.screenshot_url ? 6 : 0}>
      {review.emotional_state && (
        <Badge colorScheme={emotionColor(review.emotional_state)}>
          {formatEmotionalState(review.emotional_state)}
        </Badge>
      )}
      {review.setup_type && (
        <Text fontSize="xs">Setup: {review.setup_type}</Text>
      )}
      {review.followed_plan !== null && review.followed_plan !== undefined && (
        <Text fontSize="xs">
          Followed Plan: {review.followed_plan ? "Yes" : "No"}
        </Text>
      )}
    </HStack>
    {review.notes && (
      <Text fontSize="xs" mt={1} color={reviewNoteColor}>
        {review.notes}
      </Text>
    )}
  </Box>
);

/**
 * Reusable dialog for the day / week / month "Trades Closed in ..." popups.
 *
 * - `headerText` is the human-readable date or range
 * - `emptyText` is shown when no trades exist for the range
 * - `totalTrades` / `totalPnl` drive the footer stats
 * - `reviews` (optional) — when supplied (day variant), renders the reviews
 *   panel below the trade table
 */
const TradesOfRangeDialog = ({
  isOpen,
  onClose,
  isLoading,
  headerText,
  emptyText,
  hasTrades,
  trades,
  totalTrades,
  totalPnl,
  reviews,
  onScreenshotClick,
  pnlValue,
  formatter,
  colorChange,
  preferences,
  reviewBg,
  reviewCardBg,
  reviewNoteColor,
  size = "2xl",
}) => {
  const cancelRef = useRef();

  return (
    <AlertDialog
      motionPreset="slideInBottom"
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isCentered={true}
      closeOnOverlayClick={true}
      size={size}
    >
      {isLoading ? (
        <AlertDialogOverlay>
          <AlertDialogContent>
            <Center p={8}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Center>
          </AlertDialogContent>
        </AlertDialogOverlay>
      ) : (
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <Center>{headerText}</Center>
            </AlertDialogHeader>
            <AlertDialogBody>
              {hasTrades ? (
                <>
                  <MiniTradeTable tradesList={trades} />
                  {reviews && Object.keys(reviews).length > 0 && (
                    <Box
                      mt={4}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                      bg={reviewBg}
                    >
                      <Text fontWeight="bold" fontSize="sm" mb={2}>
                        Trade Reviews
                      </Text>
                      {Object.values(reviews).map((review, idx) => (
                        <ReviewCard
                          key={idx}
                          review={review}
                          onScreenshotClick={onScreenshotClick}
                          reviewCardBg={reviewCardBg}
                          reviewNoteColor={reviewNoteColor}
                        />
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Center>
                  <Badge variant="subtle" colorScheme="red" fontSize="0.8em">
                    {emptyText}
                  </Badge>
                </Center>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Flex width="100%" justifyContent="space-between">
                <Stat flex="1">
                  <StatLabel>Total Trades</StatLabel>
                  <StatNumber>{totalTrades ?? 0}</StatNumber>
                </Stat>
                <Stat flex="1" textAlign="right">
                  <StatLabel>Total PnL</StatLabel>
                  <StatNumber color={colorChange(totalPnl ?? 0)}>
                    {pnlValue(
                      formatter(preferences.preferred_currency).format(
                        totalPnl ?? 0
                      )
                    )}
                  </StatNumber>
                </Stat>
              </Flex>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      )}
    </AlertDialog>
  );
};

export default TradesOfRangeDialog;
